import {
  Alert,
  Box,
  Flex,
  Button,
  Icon,
  Text,
  useDisclosure,
  CModal,
  CModalBody,
  ModalCloseButton,
  ModalContent,
  CModalFooter,
  CModalHeader,
  ModalOverlay,
} from '@televet/kibble-ui';
import React, { useEffect, useMemo, useState } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  PhoneNumberStatus,
  ServiceProvider,
  useUpdateOneClinicPhoneNumberMutation,
  useTwilioGetOrGenerateLoaMutation,
  useTwilioGetOrCreateHostedNumberOrderMutation,
  LoaStatus,
  TwilioHostedNumberOrderStatus,
} from 'shared/types/graphql';

type IEnableTwilioPhoneNumberProps = {
  phoneNumber: string;
  isPrimary: boolean;
  incomingCarrier: string;
  clinicPhoneNumberId: string;
  loaSignedAt?: string;
  loaSignedByUser?: string;
  loaUrl?: string | null;
  status: PhoneNumberStatus;
  provider: ServiceProvider;
  refetchPhoneNumbers: () => void;
  isClinicDataComplete: boolean;
  portingOrderId?: string | null;
};

enum ModalContentSteps {
  Hosting = 'Hosting',
  PhoneVerification = 'PhoneVerification',
  LOA = 'LOA',
}

const EnableTwilioPhoneNumber = ({
  phoneNumber,
  isPrimary,
  status,
  incomingCarrier,
  clinicPhoneNumberId,
  loaSignedAt,
  loaSignedByUser,
  loaUrl,
  provider,
  refetchPhoneNumbers,
  portingOrderId,
}: IEnableTwilioPhoneNumberProps): JSX.Element => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentModalContent, setCurrentModalContent] = useState(ModalContentSteps.Hosting);
  const [displayLastWarning, setDisplayLastWarning] = useState(false);

  const { clinicUser, currentClinicId } = useClinicUser();

  const resetState = (): void => {
    setCurrentModalContent(ModalContentSteps.Hosting);
    setDisplayLastWarning(false);
  };

  useEffect(() => {
    resetState();
  }, [currentClinicId]);

  const [twilioGetOrGenerateLoa, { data: loaData, loading: loaGetOrGenerateLoading }] =
    useTwilioGetOrGenerateLoaMutation({});
  const [twilioGetOrCreateHostedNumberOrder, { data: hostedOrderData, loading: hostedDataLoading }] =
    useTwilioGetOrCreateHostedNumberOrderMutation();

  useEffect(() => {
    if (loaUrl) {
      twilioGetOrGenerateLoa({
        variables: {
          data: {
            clinicPhoneNumberId: clinicPhoneNumberId,
            userId: clinicUser?.id ?? '',
          },
        },
      });
    }
  }, [clinicPhoneNumberId, clinicUser?.id, currentClinicId, loaUrl, phoneNumber, twilioGetOrGenerateLoa]);

  useEffect(() => {
    if (portingOrderId && status === PhoneNumberStatus.ReadyToHost && provider === ServiceProvider.Twilio) {
      twilioGetOrCreateHostedNumberOrder({
        variables: {
          data: {
            clinicPhoneNumberId: clinicPhoneNumberId,
          },
        },
      });
    }
  }, [
    clinicPhoneNumberId,
    clinicUser?.id,
    currentClinicId,
    loaUrl,
    phoneNumber,
    portingOrderId,
    provider,
    status,
    twilioGetOrCreateHostedNumberOrder,
    twilioGetOrGenerateLoa,
  ]);

  const handleEnablePhoneNumber = (): void => {
    onOpen();
  };

  const handleStartPorting = (): void => {
    resetState();
    onClose();
    updateOneClinicPhoneNumber({
      variables: {
        where: {
          id: clinicPhoneNumberId,
        },
        data: { status: PhoneNumberStatus.PortingInProgress },
      },
    });
  };

  const handleActivatePhoneNumber = async (): Promise<void> => {
    await updateOneClinicPhoneNumber({
      variables: {
        where: {
          id: clinicPhoneNumberId,
        },
        data: { isPrimary: true },
      },
    });
    refetchPhoneNumbers();
  };

  const [updateOneClinicPhoneNumber] = useUpdateOneClinicPhoneNumberMutation();

  const userEmail = clinicUser?.email;

  const canActivate = useMemo(() => {
    const isLOASigned = loaData?.twilioGetOrGenerateLOA?.status === LoaStatus.Signed;
    const isNumberHosted =
      hostedOrderData?.twilioGetOrCreateHostedNumberOrder?.status === TwilioHostedNumberOrderStatus.Completed ||
      status === PhoneNumberStatus.Hosted;

    return !isPrimary && ((isLOASigned && isNumberHosted) || status === PhoneNumberStatus.PurchasedFromTwilio);
  }, [status, isPrimary, hostedOrderData?.twilioGetOrCreateHostedNumberOrder?.status, loaData]);

  const isLOASigned = useMemo(() => {
    if (loaSignedAt && loaSignedByUser) {
      return true;
    }
    return false;
  }, [loaSignedAt, loaSignedByUser]);

  const isLOAGenerated = useMemo(() => {
    return !!loaUrl;
  }, [loaUrl]);

  const renderModalContent = (): JSX.Element => {
    switch (currentModalContent) {
      case ModalContentSteps.Hosting:
        return (
          <ModalContent>
            <CModalHeader>
              Enable Hosted Messaging
              <Text as="p">Incoming carrier: {incomingCarrier}</Text>
            </CModalHeader>

            <ModalCloseButton />
            <CModalBody>
              <Text as="p">The process of transferring the number for SMS will begin once the LOA is signed.</Text>
              <Alert
                status="error"
                marginTop={'18px'}
                height="100%"
                width="100%"
                iconProps={{ name: 'warningSign' }}
                description="Only confirm when you are ready to use Otto as your sole SMS interface."
                hideCloseButton
              />
            </CModalBody>

            <CModalBody>
              <Text as="p" mb={3}>
                In most cases, transferring the number for SMS is immediate. Once the process is complete, we will
                notify the LOA signer via email.
              </Text>

              <Flex
                borderRadius={'4px'}
                marginBottom={'12px'}
                backgroundColor="background.success"
                padding={'8px 8px 8px 12px'}
                alignItems={'center'}
              >
                <Icon name="checkmark" color={'success'} />
                <Box marginLeft={'17px'} fontWeight={'600'} borderRadius="4px">
                  Phone number verified
                </Box>
              </Flex>
              <Text as="p">Hosting Order:</Text>
              {!hostedOrderData?.twilioGetOrCreateHostedNumberOrder?.orderId && !portingOrderId ? (
                <Flex>
                  <Button
                    width="100%"
                    isLoading={hostedDataLoading}
                    onClick={(): void => {
                      twilioGetOrCreateHostedNumberOrder({
                        variables: {
                          data: { clinicPhoneNumberId: clinicPhoneNumberId },
                        },
                      });
                    }}
                    marginBottom={'12px'}
                  >
                    Create Hosted Number Order
                  </Button>
                </Flex>
              ) : (
                <Flex
                  borderRadius={'4px'}
                  marginBottom={'12px'}
                  backgroundColor="background.success"
                  padding={'8px 8px 8px 12px'}
                  alignItems={'center'}
                >
                  <Icon name="checkmark" color={'success'} />
                  <Box marginLeft={'17px'} fontWeight={'600'} borderRadius="4px">
                    <Text as="p">Hosted Number Order Created: </Text>
                    <Text as="p">{hostedOrderData?.twilioGetOrCreateHostedNumberOrder?.orderId ?? portingOrderId}</Text>
                    <Text as="p">Status: {hostedOrderData?.twilioGetOrCreateHostedNumberOrder?.status ?? ''}</Text>
                  </Box>
                </Flex>
              )}

              <Text as="p">
                A letter of authorization {loaData?.twilioGetOrGenerateLOA?.email ? 'was sent' : 'will be send'} to{' '}
                {loaData?.twilioGetOrGenerateLOA?.email ?? userEmail} and must be signed to enable hosted messaging
              </Text>
              {!loaData?.twilioGetOrGenerateLOA?.status && !loaUrl ? (
                <Flex>
                  <Button
                    width="100%"
                    isLoading={loaGetOrGenerateLoading}
                    onClick={(): void => {
                      twilioGetOrGenerateLoa({
                        variables: {
                          data: { clinicPhoneNumberId: clinicPhoneNumberId, userId: clinicUser?.id ?? '' },
                        },
                      });
                    }}
                    marginBottom={'12px'}
                  >
                    Generate and Send LOA
                  </Button>
                </Flex>
              ) : (
                <Flex
                  borderRadius="4px"
                  marginBottom="12px"
                  backgroundColor="background.success"
                  padding="8px 8px 8px 12px"
                  alignItems="center"
                >
                  <Icon name="checkmark" color="success" />
                  <Box marginLeft="17px" fontWeight="600" borderRadius="4px">
                    <Text as="p">LOA Generated and Sent: </Text>
                    <Text as="p">
                      {loaData?.twilioGetOrGenerateLOA?.url.split('/').pop() ?? loaUrl?.split('/').pop()}
                    </Text>
                    <Text as="p">Status: {loaData?.twilioGetOrGenerateLOA?.status ?? ''}</Text>
                  </Box>
                </Flex>
              )}
            </CModalBody>

            <CModalFooter marginBottom="8px" justifyContent={isLOASigned ? 'space-between' : 'flex-end'}>
              <Button
                onClick={(): void => {
                  resetState();
                  onClose();
                }}
                variant="tertiary"
              >
                {displayLastWarning ? 'Cancel' : 'Close'}
              </Button>
              {isLOASigned && (
                <Button
                  isLoading={!isLOAGenerated}
                  loadingText="Generating LOA"
                  onClick={(): void => {
                    if (displayLastWarning) {
                      handleStartPorting();
                    } else {
                      setDisplayLastWarning(true);
                    }
                  }}
                >
                  {displayLastWarning ? "Yes, I'm Sure" : 'Start Porting'}
                </Button>
              )}
            </CModalFooter>
          </ModalContent>
        );

      default:
        break;
    }
    return <></>;
  };

  const renderActionButtons = (): JSX.Element => {
    if (canActivate) {
      return (
        <Button size="sm" variant="secondary" onClick={handleActivatePhoneNumber}>
          Activate
        </Button>
      );
    }
    if (
      !isPrimary &&
      (status === PhoneNumberStatus.New || status === PhoneNumberStatus.PortingInProgress) &&
      provider === ServiceProvider.Twilio
    ) {
      return (
        <Button size="sm" onClick={handleEnablePhoneNumber}>
          Enable
        </Button>
      );
    }
    if (!isPrimary && status === PhoneNumberStatus.ReadyToHost && provider === ServiceProvider.Twilio) {
      return (
        <Button size="sm" onClick={handleEnablePhoneNumber}>
          Enable
        </Button>
      );
    }
    if (!isPrimary && status === PhoneNumberStatus.HostingNotAvailable && provider === ServiceProvider.Twilio) {
      return (
        <Button size="sm" onClick={handleStartPorting}>
          Retry Porting
        </Button>
      );
    }
    return <Box width="81px" />;
  };

  return (
    <div className="EnablePhoneNumber__" style={{ minWidth: '100px' }}>
      <CModal
        onClose={(): void => {
          resetState();
          onClose();
        }}
        isOpen={isOpen}
        isCentered
      >
        <ModalOverlay />
        {renderModalContent()}
      </CModal>
      {renderActionButtons()}
    </div>
  );
};

export default EnableTwilioPhoneNumber;
